import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import { useZohoOrgId, zohoPaymentUrl } from '../../../utils/zohoOrg';
import './wallet.css';

// Card Payments — every card charge the business has taken, each shown with the
// reference (Booking #/REQ-/invoice numbers/Top-up) and the thing it paid for,
// so a manager can look one up. Rendered on /wallet/pending beside the other
// staff sections. Read-only.
//
// Backend: GET /api/wallet/card-payments?q=&limit=&offset=
//   -> { payments: [ { payment_code, merchant_reference, source_type, source_id, kind, amount, created_at,
//        client_id, client_name, reference, relation_type, relation_id,
//        zoho_payment_id, gateway_order_ref, status, refunded_amount,
//        payment_rail, card_scheme, card_masked_pan } ], total, limit, offset }
//   payment_rail = apple_pay | samsung_pay | google_pay | saved_card | new_card
//   (the gateway's word, from the checkout row — every rail carries it, and
//   the card scheme/number, since 2026-09-10; before, only booking receipts
//   had a card and the column read "—" on every settlement).
const PAGE = 50;

const money = (n) =>
    `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (s) => {
    if (!s) return '—';
    const dt = DateTime.fromISO(s, { zone: 'Asia/Dubai' });
    return dt.isValid ? dt.toFormat('dd MMM yy, HH:mm') : '—';
};

const last4 = (pan) => {
    const digits = (pan || '').replace(/\D/g, '');
    return digits.length >= 4 ? digits.slice(-4) : '';
};

// HOW it was paid. Wallets and saved cards get a tag beside the card; a
// typed-in card is the plain case and gets none.
const RAIL_LABELS = {
    apple_pay: 'Apple Pay',
    samsung_pay: 'Samsung Pay',
    google_pay: 'Google Pay',
    saved_card: 'Saved card',
};

// Short scheme names, so the column stays one line (owner, 2026-09-10:
// "can mastercard be mc instead"). Anything unlisted shows as the gateway
// spells it.
const SCHEME_SHORT = {
    MASTERCARD: 'MC',
    AMERICAN_EXPRESS: 'AMEX',
    DINERS_CLUB_INTERNATIONAL: 'DINERS',
    DISCOVER: 'DISC',
    UNIONPAY: 'UPI',
};

const cardLabel = (row) => {
    const l4 = last4(row.card_masked_pan);
    let scheme = row.card_scheme ? String(row.card_scheme).toUpperCase() : '';
    // The reader before 2026-09-10 stored the wallet's name as the scheme
    // ('APPLE_PAY'); the tag says that now, so it is not a scheme here.
    if (/_PAY$/.test(scheme)) scheme = '';
    scheme = SCHEME_SHORT[scheme] || scheme;
    if (l4) return `${scheme ? scheme + ' ' : ''}···· ${l4}`.trim();
    return scheme;
};

const CardCell = ({ row }) => {
    const tag = RAIL_LABELS[row.payment_rail];
    const card = cardLabel(row);
    if (!tag && !card) return <span className="swallet__muted">—</span>;
    return (
        <>
            {tag && <span className={`swallet__railTag swallet__railTag--${row.payment_rail}`}>{tag}</span>}
            {card}
        </>
    );
};

// Chip label + colour by rail. booking/settlement read as money in (blue),
// top-ups as wallet credit (orange).
const typeChip = (row) => {
    if (row.source_type === 'settlement') return { label: 'Invoice', cls: 'swallet__chip--blue' };
    if (row.source_type === 'topup') return { label: 'Top-up', cls: 'swallet__chip--orange' };
    const byKind = {
        booking: 'Booking',
        booking_change: 'Booking change',
        adjustment: 'Adjustment',
        autocharge: 'Auto-charge',
    };
    return { label: byKind[row.kind] || 'Booking', cls: 'swallet__chip--blue' };
};

export default function CardPaymentsSection() {
    const [payments, setPayments] = useState([]);
    // Live vs sandbox org for the Zoho deep links — null until fetched, and
    // the id renders as plain text until then (a wrong-org link is worse).
    const zohoOrgId = useZohoOrgId();
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [query, setQuery] = useState('');
    const debounce = useRef(null);
    const reqId = useRef(0); // guards against out-of-order search responses

    const fetchPage = useCallback(async (q, offset) => {
        const mine = ++reqId.current;
        if (offset === 0) setLoading(true); else setLoadingMore(true);
        try {
            const res = await axios.get('/api/wallet/card-payments', {
                params: { q, limit: PAGE, offset },
            });
            if (mine !== reqId.current) return; // a newer search superseded this one
            const rows = res.data?.payments || [];
            setTotal(res.data?.total || 0);
            setPayments((prev) => (offset === 0 ? rows : [...prev, ...rows]));
        } catch (err) {
            if (mine !== reqId.current) return;
            toast.error('Failed to load card payments');
        } finally {
            if (mine === reqId.current) { setLoading(false); setLoadingMore(false); }
        }
    }, []);

    // Initial load + debounced re-fetch whenever the search text changes.
    useEffect(() => {
        if (debounce.current) clearTimeout(debounce.current);
        debounce.current = setTimeout(() => fetchPage(query.trim(), 0), 300);
        return () => debounce.current && clearTimeout(debounce.current);
    }, [query, fetchPage]);

    const rowKey = (r) => `${r.source_type}-${r.source_id}`;

    return (
        <div className="swallet__section">
            <div className="swallet__sectionHead">
                <h2 className="swallet__sectionTitle">Card Payments</h2>
                <span className="swallet__reconBadge">{total} total</span>
                <span className="swallet__sectionSub">
                    Every card charge taken — bookings, invoice settlements and wallet top-ups.
                    Search by code (BKG-, STL-, TOP-, LNK-), gateway ref (HHC-…), reference (Booking&nbsp;#, invoice&nbsp;no.), client, Zoho payment&nbsp;id, "apple pay", or a card's last digits.
                </span>
                <input
                    className="swallet__formInput"
                    style={{ marginTop: 10, maxWidth: 340 }}
                    type="text"
                    placeholder="Search card payments…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
            </div>

            {loading ? (
                <div className="swallet__empty">Loading…</div>
            ) : payments.length === 0 ? (
                <div className="swallet__empty">
                    {query.trim() ? 'No card payments match your search.' : 'No card payments yet.'}
                </div>
            ) : (
                <>
                    <table className="swallet__table">
                        <thead>
                            <tr>
                                <th>Ref</th>
                                <th>Date</th>
                                <th>Client</th>
                                <th>Reference</th>
                                <th>Type</th>
                                <th>Card</th>
                                <th>Amount</th>
                                <th>Zoho</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payments.map((r) => {
                                const chip = typeChip(r);
                                const refunded = Number(r.refunded_amount || 0) > 0;
                                return (
                                    <tr key={rowKey(r)}>
                                        {/* The one thing on this row a person can
                                            quote when asking about a payment. The
                                            per-rail id alone is not unique across
                                            rails, so the prefix is part of it. */}
                                        <td className="swallet__code">
                                            {r.payment_code || '—'}
                                            {/* The gateway's own name for this
                                                payment, underneath: it is the
                                                only string that ties this row
                                                to a line on the N-Genius
                                                settlement report. Absent on
                                                payments taken before we began
                                                stamping it. */}
                                            {r.merchant_reference ? (
                                                <span className="swallet__gwref">{r.merchant_reference}</span>
                                            ) : null}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.created_at)}</td>
                                        <td style={{ fontWeight: 800 }}>
                                            {r.client_id ? (
                                                <Link to={`/wallet/clients/${r.client_id}`} className="swallet__proofLink">
                                                    {r.client_name || `Client #${r.client_id}`}
                                                </Link>
                                            ) : (r.client_name || '—')}
                                        </td>
                                        <td>
                                            {r.relation_type === 'booking' ? (
                                                <Link to={`/bookings/${r.relation_id}`} className="swallet__proofLink">
                                                    {r.reference}
                                                </Link>
                                            ) : (
                                                <span>{r.reference}</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`swallet__chip ${chip.cls}`}>{chip.label}</span>
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}><CardCell row={r} /></td>
                                        <td style={{ fontWeight: 900, whiteSpace: 'nowrap' }}>
                                            {money(r.amount)}
                                            {refunded && (
                                                <div style={{ fontWeight: 700, fontSize: 12, color: '#c0392b' }}>
                                                    −{money(r.refunded_amount)} refunded
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {/* OPENS the payment in Zoho Books (owner,
                                                2026-09-07) — nobody needs the id copied, they
                                                need the receipt. The 18-digit id itself is
                                                out of the cell (owner, 2026-09-10: "rename to
                                                zoho so we have more space") and lives in the
                                                tooltip; it is still searchable. Org-aware:
                                                sandbox on a test database, live otherwise. */}
                                            {r.zoho_payment_id ? (
                                                zohoOrgId ? (
                                                    <a
                                                        className="swallet__zohoLink"
                                                        href={zohoPaymentUrl(zohoOrgId, r.zoho_payment_id)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title={`Open payment ${r.zoho_payment_id} in Zoho Books`}
                                                    >
                                                        Zoho ↗
                                                    </a>
                                                ) : (
                                                    <span className="swallet__zohoLink" title={r.zoho_payment_id}>Zoho</span>
                                                )
                                            ) : (
                                                <span
                                                    className="swallet__chip swallet__chip--red"
                                                    title="No Zoho payment recorded for this charge"
                                                >
                                                    No receipt
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    <div className="swallet__sectionSub" style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span>Showing {payments.length} of {total}</span>
                        {payments.length < total && (
                            <button
                                type="button"
                                className="swallet__btn"
                                disabled={loadingMore}
                                onClick={() => fetchPage(query.trim(), payments.length)}
                            >
                                {loadingMore ? 'Loading…' : 'Load more'}
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
